import React from 'react';
import { assertExhaustive, assertNotNil, useHasFF } from 'venn-utils';
import type { MultiPortfolioUploaderViewId } from '../types';
import { useMultiPortfolioUploaderState } from './hooks/useMultiPortfolioUploaderState';
import MultiPortfolioConfirmationStep from './MultiPortfolioConfirmationStep';
import MultiPortfolioUploadStep from './MultiPortfolioUploadStep';
import FlatMultiPortfolioUploadStep from './FlatMultiPortfolioUploadStep';
import AssignOwnershipStep from './AssignOwnershipStep';
import { Root as MultiPortfolioReviewRoot } from './components/common/MultiPortfolioReviewComponents.Root';
import { MultiHistoricalReviewStep } from './MultiHistoricalReviewStep';
import { MultiProFormaReviewStep } from './MultiProFormaReviewStep';

type MultiPortfolioUploaderProps = Readonly<{
  viewId: MultiPortfolioUploaderViewId;
}>;

const MultiPortfolioUploader = ({ viewId }: MultiPortfolioUploaderProps) => {
  const hasTaxImpactFF = useHasFF('tax_impact_ff');
  const {
    parsedData,
    uploadConfirmationData,
    goToReviewStep,
    goBackToUploadStep,
    goToUploadConfirmation,
    goToAssignOwnership,
  } = useMultiPortfolioUploaderState();

  switch (viewId) {
    case 'UPLOAD_PORTFOLIO':
      return hasTaxImpactFF ? (
        <FlatMultiPortfolioUploadStep goToReviewStep={goToReviewStep} />
      ) : (
        <MultiPortfolioUploadStep goToReviewStep={goToReviewStep} />
      );
    case 'REVIEW_PRO_FORMA_PORTFOLIO':
    case 'REVIEW_HISTORICAL_PORTFOLIO':
    case 'ASSIGN_OWNERSHIP':
      return (
        <MultiPortfolioReviewRoot
          parsedData={assertNotNil(parsedData)}
          goBackToUploadStep={goBackToUploadStep}
          goToUploadConfirmation={goToUploadConfirmation}
          goToAssignOwnership={goToAssignOwnership}
        >
          {viewId === 'REVIEW_PRO_FORMA_PORTFOLIO' && <MultiProFormaReviewStep />}
          {viewId === 'REVIEW_HISTORICAL_PORTFOLIO' && <MultiHistoricalReviewStep />}
          {viewId === 'ASSIGN_OWNERSHIP' && (
            <AssignOwnershipStep
              goBackToReviewStep={() => goToReviewStep(assertNotNil(parsedData))}
              goToUploadConfirmation={goToUploadConfirmation}
            />
          )}
        </MultiPortfolioReviewRoot>
      );
    case 'PORTFOLIO_UPLOAD_CONFIRMATION':
      return <MultiPortfolioConfirmationStep uploadConfirmationData={assertNotNil(uploadConfirmationData)} />;
    default:
      throw assertExhaustive(viewId);
  }
};

export default MultiPortfolioUploader;
